import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import MapElementProps from '../MapElementProps';
import { useAppSelector } from '../../../redux/hooks';
import { selectTiles, Tile } from '../../../redux/features/firestore/tiles/slice';
import { colorToHex } from '../../../redux/features/color/color';
import './SparkleOverlay.scss';

/**
 * SparkleOverlay — screen-space particle emitters over sparkle-flagged tiles.
 *
 * Mirrors the mobile SparkleParticles look: each emitter sits at a tile's
 * projected screen position and continuously emits small four-point stars
 * that drift outward with an upward float, spin, and twinkle as they fade.
 * Every particle gets randomized size / distance / duration / delay so the
 * emission looks organic. Emitter count is capped (nearest on-screen tiles,
 * zoom-gated) so particle load stays bounded.
 */

const SPARKLE_MAX_EMITTERS = 12;
const SPARKLE_MIN_ZOOM = 16;
const PARTICLES_PER_EMITTER = 6;

interface ParticleSpec {
    dx: number;       // total horizontal drift (px)
    dy: number;       // total vertical drift (px) — biased upward
    size: number;     // glyph size (px)
    duration: number; // one emission cycle (ms)
    delay: number;    // stagger before first emission (ms)
    spin: number;     // total rotation (deg)
    white: boolean;   // bright white accent instead of tile color
}

function makeSpecs(): ParticleSpec[] {
    return Array.from({ length: PARTICLES_PER_EMITTER }, (_, i) => {
        // Fan out around the circle with jitter; bias drift upward so the
        // sparkles float like embers rather than exploding symmetrically.
        const angle =
            (i / PARTICLES_PER_EMITTER) * Math.PI * 2 + (Math.random() - 0.5) * 0.9;
        const travel = 14 + Math.random() * 18;
        return {
            dx: Math.cos(angle) * travel,
            dy: Math.sin(angle) * travel - (6 + Math.random() * 10),
            size: 6 + Math.random() * 6,
            duration: 950 + Math.random() * 650,
            delay: Math.random() * 1200,
            spin: (Math.random() < 0.5 ? -1 : 1) * (90 + Math.random() * 180),
            white: i % 3 === 0, // every third particle is a bright white glint
        };
    });
}

interface Emitter {
    id: string;
    x: number;
    y: number;
    color: string;
}

// Four-point star matching the mobile star-four-points glyph.
function Star({ size, color, glow }: { size: number; color: string; glow: string }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            style={{ display: 'block', filter: `drop-shadow(0 0 3px ${glow})` }}
            aria-hidden="true"
        >
            <path
                fill={color}
                d="M12 0 L14.8 9.2 L24 12 L14.8 14.8 L12 24 L9.2 14.8 L0 12 L9.2 9.2 Z"
            />
        </svg>
    );
}

function EmitterView({ emitter, specs }: { emitter: Emitter; specs: ParticleSpec[] }) {
    return (
        <div className="sparkle-overlay__emitter" style={{ left: emitter.x, top: emitter.y }}>
            {specs.map((spec, i) => (
                <div
                    key={i}
                    className="sparkle-overlay__particle"
                    style={{
                        // Custom properties drive the shared keyframes
                        ['--dx' as any]: `${spec.dx}px`,
                        ['--dy' as any]: `${spec.dy}px`,
                        ['--spin' as any]: `${spec.spin}deg`,
                        animationDuration: `${spec.duration}ms`,
                        animationDelay: `${spec.delay}ms`,
                    }}
                >
                    <Star
                        size={spec.size}
                        color={spec.white ? '#FFFFFF' : emitter.color}
                        glow={spec.white ? 'rgba(255,255,255,0.9)' : emitter.color}
                    />
                </div>
            ))}
        </div>
    );
}

const SparkleOverlay = ({ map }: MapElementProps) => {
    const tiles: { [lng: number]: { [lat: number]: Tile } } = useAppSelector(selectTiles);
    const [emitters, setEmitters] = useState<Emitter[]>([]);
    // Specs are generated once per emitter id so camera moves don't reshuffle
    // the animation mid-flight.
    const specsRef = useRef<Map<string, ParticleSpec[]>>(new Map());
    const tilesRef = useRef(tiles);
    tilesRef.current = tiles;

    useEffect(() => {
        if (!map) return;

        let raf = 0;
        const update = () => {
            raf = 0;
            if (map.getZoom() < SPARKLE_MIN_ZOOM) {
                setEmitters((prev) => (prev.length ? [] : prev));
                return;
            }
            const sparkleTiles: { lng: number; lat: number; color: string }[] = [];
            const t = tilesRef.current;
            for (const lng in t) {
                for (const lat in t[lng]) {
                    const tile = t[lng][lat];
                    if (tile.sparkle !== true) continue;
                    sparkleTiles.push({
                        lng: tile.coordinate.longitude,
                        lat: tile.coordinate.latitude,
                        color: colorToHex(tile.color),
                    });
                }
            }
            if (sparkleTiles.length === 0) {
                setEmitters((prev) => (prev.length ? [] : prev));
                return;
            }
            const center = map.getCenter();
            const container = map.getContainer();
            const w = container.clientWidth;
            const h = container.clientHeight;
            const next = sparkleTiles
                .sort(
                    (a, b) =>
                        (a.lng - center.lng) ** 2 + (a.lat - center.lat) ** 2 -
                        ((b.lng - center.lng) ** 2 + (b.lat - center.lat) ** 2)
                )
                .slice(0, SPARKLE_MAX_EMITTERS)
                .map((s) => {
                    const p = map.project([s.lng, s.lat]);
                    return { id: `${s.lng}:${s.lat}`, x: p.x, y: p.y, color: s.color };
                })
                .filter((e) => e.x >= -20 && e.x <= w + 20 && e.y >= -20 && e.y <= h + 20);
            setEmitters(next);
        };

        const schedule = () => {
            if (!raf) raf = requestAnimationFrame(update);
        };

        schedule();
        map.on('move', schedule);
        map.on('zoom', schedule);
        return () => {
            map.off('move', schedule);
            map.off('zoom', schedule);
            if (raf) cancelAnimationFrame(raf);
        };
    }, [map, tiles]);

    if (!map || emitters.length === 0) return null;

    // Keep spec cache bounded to the visible emitter set
    const visibleIds = new Set(emitters.map((e) => e.id));
    specsRef.current.forEach((_, id) => {
        if (!visibleIds.has(id)) specsRef.current.delete(id);
    });
    for (const e of emitters) {
        if (!specsRef.current.has(e.id)) specsRef.current.set(e.id, makeSpecs());
    }

    return createPortal(
        <div className="sparkle-overlay">
            {emitters.map((e) => (
                <EmitterView key={e.id} emitter={e} specs={specsRef.current.get(e.id)!} />
            ))}
        </div>,
        map.getContainer()
    );
};

export default SparkleOverlay;
